
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Libraries from 'plaid-threads/Icons/Libraries';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  description: 'Enrich your transaction data with merchant, category, and location insights.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Enrich'}>{metadata.description}</Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Enrich'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/enrich/' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Enrich (US/CA only) makes it easy to work with transaction data generated by your own banking products or retrieved from other
non-Plaid sources. Enrich brings context and meaning to your data by enriching it with detailed merchant, category, and
location information. The enriched data can be used to build apps and features that help users manage their cash flow,
earn rewards, and discover other financial products to help them reach their financial goals.`}</p>
    <YouTubeEmbed videoId='2-iJtjzE_6U' alignment='full' title='Plaid Enrich in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Enrich works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "sample-enrich-data"
    }}>{`Sample Enrich data`}</h4>
    <p>{`Below is an example of the enrichments Plaid can provide for a transaction.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false #Sample transaction:",
        "switcher": "false",
        "#Sample": true,
        "transaction:": true
      }}>{`PURCHASE WM SUPERCENTER #1700 POWAY CAUS
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Enrichments returned by Plaid:",
        "switcher": "false",
        "#Enrichments": true,
        "returned": true,
        "by": true,
        "Plaid:": true
      }}>{`{
  "counterparties": [{
    "name": "Walmart",
    "type": "merchant",
    "logo_url": "https://plaid-merchant-logos.plaid.com/walmart_1100.png",
    "website": "walmart.com",
    "entity_id": "O5W5j4dN9OR3E6ypQmjdkWZZRoXEzVMz2ByWM"
  }],
  "entity_id": "O5W5j4dN9OR3E6ypQmjdkWZZRoXEzVMz2ByWM",
  "location": {
    "address": "13425 Community Rd",
    "city": "Poway",
    "region": "CA",
    "country": "US",
    "postal_code": "92064",
    "store_number": "1700",
    "lat": 32.959068,
    "lon": -117.037666
  },
  "logo_url": "https://plaid-merchant-logos.plaid.com/walmart_1100.png",
  "merchant_name": "Walmart",
  "payment_channel": "in store",
  "personal_finance_category": {
    "detailed": "GENERAL_MERCHANDISE_SUPERSTORES",
    "primary": "GENERAL_MERCHANDISE"
  },
  "personal_finance_category_icon_url": "https://plaid-category-icons.plaid.com/PFC_GENERAL_MERCHANDISE.png",
  "website": "walmart.com"
}
`}</code></pre>
    <h4 {...{
      "id": "testing-enrich"
    }}>{`Testing Enrich`}</h4>
    <p>{`Enrich is available for use in all environments.`}</p>
    <p>{`Sandbox: Test the integration with sample transaction data. Send a request with data from this `}<a parentName="p" {...{
        "href": "https://plaid.com/documents/enrich_sandbox_preset_transactions.csv"
      }}>{`preset list`}</a>{` of sample transactions to receive a response.`}</p>
    <p>{`Production: Get real enrichments for all your transactions.`}</p>
    <ul>
      <li parentName="ul">{`If you don't yet have a Plaid plan, or are on a Pay-as-you-go or Growth plan, you can get access to Enrich by submitting a `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/onboarding/"
        }}>{`Production request`}</a>{`.`}</li>
    </ul>
    <p>{`When testing Enrich in Production, make sure to use input data and amounts that are as realistic as possible, as all input fields, not just `}<inlineCode parentName="p">{`description`}</inlineCode>{`, are used to analyze transactions.`}</p>
    <p>{`For more details and best practices on testing Enrich, see the `}<a parentName="p" {...{
        "href": "[redacted]#########################################################################################################"
      }}>{`Enrich Testing Guide`}</a>{`.`}</p>
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <ol>
      <li parentName="ol">{`Prepare your transaction data for enrichment. Plaid accepts a maximum of 100 transactions per request. For each transaction, include the `}<inlineCode parentName="li">{`description`}</inlineCode>{`, `}<inlineCode parentName="li">{`amount`}</inlineCode>{`, `}<inlineCode parentName="li">{`direction`}</inlineCode>{`, `}<inlineCode parentName="li">{`iso_currency_code`}</inlineCode>{` and `}<inlineCode parentName="li">{`id`}</inlineCode>{`. For the best results, provide values in the optional `}<inlineCode parentName="li">{`location`}</inlineCode>{`, `}<inlineCode parentName="li">{`mcc`}</inlineCode>{`, and/or `}<inlineCode parentName="li">{`date_posted`}</inlineCode>{` fields, if available. At the request level, you must also specify the required `}<inlineCode parentName="li">{`account_type`}</inlineCode>{` (`}<inlineCode parentName="li">{`depository`}</inlineCode>{` or `}<inlineCode parentName="li">{`credit`}</inlineCode>{`).`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/transactions/enrich`}</inlineCode>{` to send Plaid your transactions.`}</li>
      <li parentName="ol">{`Plaid will provide an API response with all available enrichments for the transactions.`}</li>
    </ol>
    <h4 {...{
      "id": "enrich-pricing"
    }}>{`Enrich pricing`}</h4>
    <p>{`Enrich is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flexible-fee"
      }}>{`flexible fee model`}</a>{`; you will be billed based on the number of transactions sent for enrichment. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "sample-enrich-data",
        level: 4,
        title: "Sample Enrich data",
      },
{
        id: "testing-enrich",
        level: 4,
        title: "Testing Enrich",
      },
{
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "enrich-pricing",
        level: 4,
        title: "Enrich pricing",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
